import FiltersService from "@/services/FiltersService";
import { defineStore } from "pinia";
import { useAuthStore } from "./auth.store";
import { i18n } from "@/main";
import { isSG } from "@/config/appVersion";

const SG = !!isSG;

export const useStatisticFiltersStore = defineStore({
    id: "statisticFilter",
    state: () => ({
        disabledFields: {
            resolution: false,
            dateFrom: false,
            dateTo: false,
            statName: false,
            division: false,
            violations: false,
        },
        filters: {
            dateTimeStart: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
            dateTimeEnd: new Date().toISOString(),
            statResolutionUnit: "hour",
            statResolutionValue: 1,
        },
        locations: [],
        laneNumbers: [],
        vehicleCategories: [2, 3, 5, 7, 8, 9, 10, 11, "Brak"],

        // ✅ SG: bez overspeed
        violations: {
            overload: i18n.global.t("permissible-load"),
            overheight: i18n.global.t("permissible-height"),
            ...(SG ? {} : { overspeed: i18n.global.t("speed") }),
            overlane: i18n.global.t("bypassing"),
            wrongDirection: i18n.global.t("wrong-way"),
        },

        // ✅ SG: bez taba overspeed
        preselectTabs: {
            basic: "basic",
            current: "current",
            last24: "last24h",
            last7: "last7days",
            last31: "last31days",
            ...(SG ? {} : { overSpeed: "overspeed" }),
        },

        preselectActiveTab: "",

        // ✅ jeśli “speed” jest używane tylko dla overspeed, wywalamy w SG
        orderByColumns: {
            createdAt: "measure-time",
            locationId: "location",
            plateLpr: "short-plate-no",
            ...(SG ? {} : { speed: "speed" }),
            totalLoad: "total-weight",
            vehLength: "length",
            vehHeight: "height",
            numberOfAxles: "no-of-axles",
            ctg8p1: "vehicle-type",
            overloadSA: "single-axle",
            overloadMA: "multiple-axle",
            overloadTL: "dmc",
        },

        resolution: {
            minute: i18n.global.t("10minutes"),
            hour: i18n.global.t("hour"),
            day: i18n.global.t("days"),
            month: i18n.global.t("months"),
            year: i18n.global.t("year"),
        },

        // ✅ SG: bez statystyk prędkości (min/avg/max speed)
        statName: {
            traffic: i18n.global.t("traffic"),
            trafficDensity: i18n.global.t("traffic-density"),
            ...(SG
                ? {}
                : {
                    minSpeed: i18n.global.t("min-speed"),
                    avgSpeed: i18n.global.t("avg-speed"),
                    maxSpeed: i18n.global.t("max-speed"),
                }),
            minTotalLoad: i18n.global.t("min-total-load"),
            avgTotalLoad: i18n.global.t("avg-total-load"),
            maxTotalLoad: i18n.global.t("max-total-load"),
        },

        // ✅ SG: bez groupBy speedRange / overSpeed
        groupBy: {
            lane: i18n.global.t("lane"),
            category: i18n.global.t("category8p1"),
            violation: i18n.global.t("violation"),
            ...(SG ? {} : { speedRange: i18n.global.t("speed-range"), overSpeed: i18n.global.t("over-speed") }),
        },

        graphType: {
            barPlot: i18n.global.t("bar-plot"),
            linePlot: i18n.global.t("line-plot"),
        },

        directionColumns: { desc: "ascending", asc: "descending" },

        authStore: useAuthStore(),
        requireFields: [],
        error: null,
    }),

    getters: {
        items: (state) => state.filters,
        selectedLocation: (state) => {
            if (state.filters.locations) {
                const ids = state.filters.locations.map(Number);
                return state.locations.filter((location) => ids.includes(Number(location.id)));
            }
            return [];
        },
    },

    actions: {
        async getLocations() {
            if (this.locations.length === 0) {
                try {
                    const data = await FiltersService.getLocations();
                    this.locations = data;

                    const lanesSet = new Set();
                    this.locations.forEach((location) => {
                        const laneCount = location.lanes.length;
                        for (let i = 1; i <= laneCount; i++) lanesSet.add(i);
                    });

                    this.laneNumbers = [...lanesSet].sort((a, b) => a - b);
                } catch (error) {
                    console.error("Błąd pobierania lokalizacji:", error);
                }
            }
        },

        checkAllLocations() {
            const allIds = this.locations
                .map((item) => Number(item.id))
                .sort((a, b) => a - b);

            if (!Array.isArray(this.filters.locations) || this.filters.locations.length !== allIds.length) {
                this.filters.locations = [...allIds];
            } else {
                delete this.filters.locations;
            }
        },

        setPreselectTab(index) {
            // ✅ SG: jeżeli ktoś spróbuje ustawić overspeed z kodu / URL -> fallback na basic
            if (SG && index === "overSpeed") {
                index = "basic";
            }

            this.filters = { ctg8p1: [2, 3, 5, 7, 8, 9, 10, 11] };
            const date = new Date();
            this.preselectActiveTab = index;

            switch (index) {
                case "current": {
                    date.setHours(date.getHours() - 4);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.statResolutionUnit = "minute";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = true;
                    this.disabledFields.dateFrom = true;
                    this.disabledFields.dateTo = true;
                    this.disabledFields.statName = true;
                    this.disabledFields.division = false;
                    this.disabledFields.violations = false;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "lane";
                    this.filters.graphType = "barPlot";
                    break;
                }

                case "last24": {
                    date.setDate(date.getDate() - 1);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.statResolutionUnit = "hour";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = true;
                    this.disabledFields.dateFrom = true;
                    this.disabledFields.dateTo = false;
                    this.disabledFields.statName = false;
                    this.disabledFields.division = false;
                    this.disabledFields.violations = false;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "lane";
                    this.filters.graphType = "barPlot";
                    break;
                }

                case "last7": {
                    date.setDate(date.getDate() - 7);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.statResolutionUnit = "hour";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = true;
                    this.disabledFields.dateFrom = true;
                    this.disabledFields.dateTo = false;
                    this.disabledFields.statName = false;
                    this.disabledFields.division = false;
                    this.disabledFields.violations = false;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "lane";
                    this.filters.graphType = "barPlot";
                    break;
                }

                case "last31": {
                    date.setMonth(date.getMonth() - 1);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.statResolutionUnit = "day";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = true;
                    this.disabledFields.dateFrom = true;
                    this.disabledFields.dateTo = false;
                    this.disabledFields.statName = false;
                    this.disabledFields.division = false;
                    this.disabledFields.violations = false;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "lane";
                    this.filters.graphType = "barPlot";
                    break;
                }

                // ✅ case overSpeed całkowicie usunięty dla SG (i w ogóle kontrolowany przez preselectTabs)
                case "overSpeed": {
                    if (SG) {
                        // bezpieczeństwo: jeśli jednak tu trafi, zrób basic
                        this.setPreselectTab("basic");
                        return;
                    }

                    date.setDate(date.getDate() - 1);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.overspeed = true;
                    this.filters.statResolutionUnit = "hour";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = false;
                    this.disabledFields.dateFrom = false;
                    this.disabledFields.dateTo = false;
                    this.disabledFields.statName = false;
                    this.disabledFields.division = true;
                    this.disabledFields.violations = true;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "speedRange";
                    this.filters.graphType = "barPlot";
                    break;
                }

                case "basic":
                default: {
                    date.setDate(date.getDate() - 1);
                    this.filters.dateTimeStart = date.toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    this.filters.statResolutionUnit = "hour";
                    this.filters.statResolutionValue = 1;
                    this.disabledFields.resolution = false;
                    this.disabledFields.dateFrom = false;
                    this.disabledFields.dateTo = false;
                    this.disabledFields.statName = false;
                    this.disabledFields.division = false;
                    this.disabledFields.violations = false;
                    this.filters.statName = "traffic";
                    this.filters.groupBy = "lane";
                    this.filters.graphType = "barPlot";
                }
            }
        },

        addItem(name, value) {
            switch (name) {
                case "LocationList": {
                    if (this.filters.locations) {
                        if (!this.filters.locations.includes(value)) this.filters.locations.push(value);
                        else this.filters.locations.splice(this.filters.locations.indexOf(value), 1);
                    } else {
                        this.filters.locations = [value];
                    }
                    if (this.filters.locations.length === 0) delete this.filters.locations;
                    break;
                }

                case "VehicleCategories": {
                    if (this.filters.ctg8p1) {
                        const v = this.vehicleCategories[value];
                        if (!this.filters.ctg8p1.includes(v)) this.filters.ctg8p1.push(v);
                        else this.filters.ctg8p1.splice(this.filters.ctg8p1.indexOf(v), 1);
                    } else {
                        this.filters.ctg8p1 = [this.vehicleCategories[value]];
                    }
                    if (this.filters.ctg8p1.length === 0) delete this.filters.ctg8p1;
                    break;
                }

                case "LaneNumbers": {
                    if (this.filters.laneNumbers) {
                        const v = this.laneNumbers[value];
                        if (!this.filters.laneNumbers.includes(v)) this.filters.laneNumbers.push(v);
                        else this.filters.laneNumbers.splice(this.filters.laneNumbers.indexOf(v), 1);
                    } else {
                        this.filters.laneNumbers = [this.laneNumbers[value]];
                    }
                    if (this.filters.laneNumbers.length === 0) delete this.filters.laneNumbers;
                    break;
                }

                case "ViolationsAll": {
                    const filters = this.filters;
                    const allChecked = !Object.keys(this.violations).some((key) => filters[key] === undefined);

                    if (!allChecked) {
                        Object.keys(this.violations).forEach((key) => (filters[key] = true));
                    } else {
                        Object.keys(this.violations).forEach((key) => delete filters[key]);
                    }
                    break;
                }

                case "Resolution": {
                    switch (value) {
                        case "minute":
                            this.filters.statResolutionUnit = "minute";
                            this.filters.statResolutionValue = 10;
                            break;
                        case "day":
                            this.filters.statResolutionUnit = "day";
                            this.filters.statResolutionValue = 1;
                            break;
                        case "month":
                            this.filters.statResolutionUnit = "month";
                            this.filters.statResolutionValue = 1;
                            break;
                        case "year":
                            this.filters.statResolutionUnit = "year";
                            this.filters.statResolutionValue = 1;
                            break;
                        case "hour":
                        default:
                            this.filters.statResolutionUnit = "hour";
                            this.filters.statResolutionValue = 1;
                            break;
                    }
                    break;
                }

                default: {
                    this.filters[name] = value;
                }
            }
        },

        removeItem(name) {
            switch (name) {
                case "rangeOfDate": {
                    this.filters.dateTimeStart = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString();
                    this.filters.dateTimeEnd = new Date().toISOString();
                    break;
                }

                case "violations": {
                    const filters = this.filters;
                    Object.keys(this.violations).forEach((key) => delete filters[key]); // ✅ bez overspeed w SG
                    break;
                }

                case "vehicleData": {
                    delete this.filters.plateLpr;
                    delete this.filters.similarity;
                    delete this.filters.minSpeed;
                    delete this.filters.maxSpeed;
                    delete this.filters.minTotalLoad;
                    delete this.filters.maxTotalLoad;
                    delete this.filters.minVehLength;
                    delete this.filters.maxVehLength;
                    delete this.filters.minVehHeight;
                    delete this.filters.maxVehHeight;
                    delete this.filters.minNumberOfAxles;
                    delete this.filters.maxNumberOfAxles;
                    delete this.filters.minOverloadSA;
                    delete this.filters.maxOverloadSA;
                    delete this.filters.minOverloadMA;
                    delete this.filters.maxOverloadMA;
                    delete this.filters.minOverloadTL;
                    delete this.filters.maxOverloadTL;
                    break;
                }

                case "resolution": {
                    delete this.filters.statResolutionUnit;
                    delete this.filters.statResolutionValue;
                    break;
                }

                default: {
                    delete this.filters[name];
                }
            }
        },

        checkRequireData() {
            this.requireFields = [];
            this.error = false;
            this.checkRequireData1();
            this.checkRequireData2();
            this.checkRequireData3();
        },

        checkRequireData1(withClear) {
            if (withClear) {
                this.requireFields = [];
                this.error = false;
            }

            if (!this.filters.dateTimeStart) this.requireFields.push("dateTimeStart");
            if (!this.filters.dateTimeEnd) this.requireFields.push("dateTimeEnd");
            if (!this.filters.locations) this.requireFields.push("locations");
            if (!this.filters.statResolutionUnit) this.requireFields.push("statResolutionUnit");
            if (!this.filters.statResolutionValue) this.requireFields.push("statResolutionValue");

            if (this.requireFields.length) {
                this.error = true;
                this.setErrors(i18n.global.t("required-parameter-missing"));
            }
        },

        checkRequireData2(withClear) {
            if (withClear) {
                this.requireFields = [];
                this.error = false;
            }
        },

        checkRequireData3(withClear) {
            if (withClear) {
                this.requireFields = [];
                this.error = false;
            }
        },

        setErrors(error) {
            this.authStore.errorMessage = error;
        },
    },
});

// ✅ HMR: Vite lub Webpack/Vue CLI (bez crashy)
// if (typeof import.meta !== "undefined" && import.meta.hot) {
//     import.meta.hot.accept(acceptHMRUpdate(useStatisticFiltersStore, import.meta.hot));
// } else if (typeof module !== "undefined" && module.hot) {
//     module.hot.accept(acceptHMRUpdate(useStatisticFiltersStore, module.hot));
// }
